import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { BookOpen, FileText, ChefHat, Download, ArrowRight, Play, Clock, Calendar, Users, Star, HelpCircle, MessageCircle, Phone, ExternalLink, Video, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Resources = () => {
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [popularRecipes, setPopularRecipes] = useState([]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [blogsRes, recipesRes] = await Promise.all([
          axios.get(`${API_URL}/api/blog?limit=3`),
          axios.get(`${API_URL}/api/recipes?limit=4`)
        ]);
        setRecentBlogs(blogsRes.data.slice(0, 3));
        setPopularRecipes(recipesRes.data.slice(0, 4));
      } catch (e) {
        console.error('Error fetching content:', e);
      }
    };
    fetchContent();
  }, []);

  const resourceCategories = [
    {
      icon: BookOpen,
      title: 'Blog',
      description: 'Tips, guides, and stories from our freeze drying community. Learn from experts and fellow enthusiasts.',
      link: '/blog',
      color: 'from-blue-500 to-blue-600',
      stats: '50+ Articles'
    },
    {
      icon: ChefHat,
      title: 'Recipe Collection',
      description: 'Delicious recipes perfect for freeze drying. From fruits and vegetables to complete meals.',
      link: '/recipes',
      color: 'from-orange-500 to-orange-600',
      stats: '100+ Recipes'
    },
    {
      icon: FileText,
      title: 'Documents & Manuals',
      description: 'User guides, quick start manuals, maintenance tips, and technical specifications.',
      link: '/documents',
      color: 'from-green-500 to-green-600',
      stats: 'All Models'
    },
    {
      icon: Play,
      title: 'Video Gallery',
      description: 'Watch tutorials, customer stories, and tips to get the most out of your freeze dryer.',
      link: '/resources/videos',
      color: 'from-red-500 to-red-600',
      stats: 'Watch & Learn'
    },
    {
      icon: HelpCircle,
      title: 'FAQ',
      description: 'Find answers to frequently asked questions about freeze drying and our products.',
      link: '/resources/faq',
      color: 'from-purple-500 to-purple-600',
      stats: 'Quick Answers'
    },
    {
      icon: Download,
      title: 'Recipe Books',
      description: 'Download our curated recipe books with freeze drying tips and preparation guides.',
      link: '/documents?category=recipe-book',
      color: 'from-teal-500 to-teal-600',
      stats: 'Free Downloads'
    },
    {
      icon: GraduationCap,
      title: 'Freeze Dry Academy',
      description: 'Go deeper with expert-led courses and training on freeze drying techniques, business tips and best practices.',
      link: 'https://www.freezedryacademy.com',
      external: true,
      color: 'from-[#003456] to-[#CE8946]',
      stats: 'Learn & Train'
    }
  ];

  const quickLinks = [
    { title: 'Getting Started Guide', desc: 'New to freeze drying? Start here', link: '/blog', icon: Play },
    { title: 'Top 10 Recipes', desc: 'Most popular recipes to try', link: '/recipes', icon: Star },
    { title: 'User Manuals', desc: 'Download your product manual', link: '/documents?category=user-manual', icon: FileText },
    { title: 'Maintenance Tips', desc: 'Keep your machine running smoothly', link: '/documents?category=maintenance-guide', icon: HelpCircle },
    { title: 'Customer Support', desc: 'Get help from our team', link: '/customer-support', icon: MessageCircle },
    { title: 'Contact Us', desc: 'Reach out with questions', link: '/contact', icon: Phone }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      {/* Hero */}
      <div className="pt-44 pb-20 bg-gradient-to-br from-[#003456] via-[#002845] to-[#002B47] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#CE8946] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Resources & Learning Center
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Everything you need to get the most out of your D-Freeze freeze dryer. 
            Tutorials, recipes, manuals, and expert tips.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/blog">
              <Button className="bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] font-semibold px-6">
                <BookOpen className="h-4 w-4 mr-2" />
                Read Blog
              </Button>
            </Link>
            <Link to="/recipes">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456]">
                <ChefHat className="h-4 w-4 mr-2" />
                Browse Recipes
              </Button>
            </Link>
          </div>
        </div>
      </div>
      
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {/* Main Resource Categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {resourceCategories.map((cat, i) => {
              const cardInner = (
                <>
                  <div className={`h-2 bg-gradient-to-r ${cat.color}`}></div>
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-14 h-14 bg-gradient-to-r ${cat.color} rounded-xl flex items-center justify-center`}>
                        <cat.icon className="h-7 w-7 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-400 bg-gray-100 px-3 py-1 rounded">
                        {cat.stats}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-[#0a0203] mb-3 group-hover:text-[#003456] transition-colors">
                      {cat.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{cat.description}</p>
                    <span className="text-[#003456] font-semibold flex items-center gap-2">
                      {cat.external ? 'Visit' : 'Explore'} {cat.external ? <ExternalLink className="h-4 w-4" /> : <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />}
                    </span>
                  </div>
                </>
              );
              const cls = "bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all group";
              return cat.external ? (
                <a key={i} href={cat.link} target="_blank" rel="noopener noreferrer" className={cls} data-testid="resource-academy-card">
                  {cardInner}
                </a>
              ) : (
                <Link key={i} to={cat.link} className={cls}>
                  {cardInner}
                </Link>
              );
            })}
          </div>
          
          {/* Quick Links Grid */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Quick Links</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {quickLinks.map((item, i) => (
                <Link 
                  key={i} 
                  to={item.link}
                  className="bg-white p-5 rounded-xl hover:bg-[#003456] group transition-all duration-300 shadow-sm hover:shadow-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#F8FAFC] rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                      <item.icon className="h-5 w-5 text-[#003456] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#0a0203] group-hover:text-white transition-colors">{item.title}</h4>
                      <p className="text-sm text-gray-500 group-hover:text-white/70 transition-colors">{item.desc}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Blog Posts */}
          {recentBlogs.length > 0 && (
            <div className="mb-16">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#0a0203]">Latest from the Blog</h2>
                <Link to="/blog" className="text-[#003456] font-medium hover:underline flex items-center gap-1">
                  View All <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {recentBlogs.map((post, i) => (
                  <Link 
                    key={i}
                    to={`/blog/${post.slug}`}
                    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                  >
                    {post.featured_image && (
                      <div className="h-40 overflow-hidden">
                        <img 
                          src={post.featured_image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <Calendar className="h-3 w-3" />
                        {new Date(post.published_at || post.created_at).toLocaleDateString()}
                      </div>
                      <h3 className="font-semibold text-[#0a0203] group-hover:text-[#003456] transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Popular Recipes */}
          {popularRecipes.length > 0 && (
            <div className="mb-16">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#0a0203]">Popular Recipes</h2>
                <Link to="/recipes" className="text-[#003456] font-medium hover:underline flex items-center gap-1">
                  View All <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {popularRecipes.map((recipe, i) => (
                  <Link 
                    key={i}
                    to={`/recipes/${recipe.slug}`}
                    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                  >
                    <div className="h-32 overflow-hidden">
                      <img 
                        src={recipe.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'} 
                        alt={recipe.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-sm text-[#0a0203] group-hover:text-[#003456] transition-colors line-clamp-2">
                        {recipe.title}
                      </h3>
                      {recipe.prep_time && (
                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
                          <Clock className="h-3 w-3" />
                          {recipe.prep_time} mins
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Help CTA */}
          <div className="bg-gradient-to-r from-[#003456] to-[#002B47] rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Need Help Getting Started?
            </h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Our team of freeze drying experts is here to help you every step of the way.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/customer-support">
                <Button className="bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] font-semibold">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Get Support
                </Button>
              </Link>
              <a href="https://freezedryersupport.com" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456]">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Customer Portal
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Resources;
